import { useCallback, useMemo } from "react";

import { CurrencyType, type InputMaybe, type StaticRoomPriceParamsInput } from "@/lib/gql/__generated__/graphql";

import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";
import { useFetchPlanStaticRoomPriceParams } from "@/components/features/items/edit/plan/_shared/model/repository/query/fetch-plan-static-room-price-params";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { useProductDayTripPlanFormModelContext } from "../../../../context";

export const usePlanRoomStaticPrice = () => {
	const { viewModel, onSetViewModel } = useProductDayTripPlanFormModelContext();
	const { hotelID, staticPriceParams, availableRoomIDs } = viewModel;

	const { onFetchStaticRoomPrices } = useFetchPlanStaticRoomPriceParams({
		queryVariables: { hotelID, roomIDs: availableRoomIDs.value.map((item) => item.value) },
	});

	const deactivateStaticPriceParams = useCallback(
		(_availableRoomIDs?: string[]) => {
			onSetViewModel([
				{
					key: "staticPriceParams",
					value: undefined,
				},
				{
					key: "availableRoomIDs",
					value: {
						isEdited: false,
						hasError: false,
						value: _availableRoomIDs
							? _availableRoomIDs.map((item) => ({
									isEdited: false,
									value: item,
								}))
							: availableRoomIDs.value.map((item) => ({
									isEdited: false,
									value: item.value,
								})),
					},
				},
			]);
		},
		[availableRoomIDs.value, onSetViewModel],
	);

	const inUse = useMemo(() => !!staticPriceParams?.staticRoomPrices, [staticPriceParams]);

	const initializeStaticPriceParams = useCallback(
		async (_availableRoomIDs?: string[]) => {
			const { data } = await onFetchStaticRoomPrices({
				hotelID,
				roomIDs: _availableRoomIDs ?? availableRoomIDs.value.map((item) => item.value),
			});

			if (!data || data?.planStaticRoomPriceParams.staticRoomPriceParams.staticRoomPrices.length === 0) {
				notifyUserEventInfo({
					variant: "error",
					title: "予約可能な部屋を1つ選択してください",
				});
				deactivateStaticPriceParams(_availableRoomIDs);
				return;
			}

			onSetViewModel([
				{
					key: "staticPriceParams",
					value: {
						staticRoomPrices: {
							isEdited: false,
							hasError: false,
							value: (data?.planStaticRoomPriceParams.staticRoomPriceParams.staticRoomPrices ?? []).map(
								(item) => {
									return {
										roomID: {
											isEdited: false,
											value: item.roomID,
										},
										roomName: {
											isEdited: false,
											value: item.roomName,
										},
										prices: {
											isEdited: false,
											hasError: false,
											value: item.prices?.map((price) => {
												return {
													adultNum: {
														isEdited: false,
														value: price.adultNum,
													},
													price: {
														amount: {
															isEdited: false,
															value: price.price.amount,
														},
														currencyType: {
															isEdited: false,
															value: price.price.currencyType,
														},
													},
												};
											}),
										},
									};
								},
							),
						},
					},
				},
				{
					key: "availableRoomIDs",
					value: {
						isEdited: false,
						hasError: false,
						value: _availableRoomIDs
							? _availableRoomIDs.map((item) => ({
									isEdited: false,
									value: item,
								}))
							: availableRoomIDs.value.map((item) => ({
									isEdited: false,
									value: item.value,
								})),
					},
				},
			]);
		},
		[availableRoomIDs.value, deactivateStaticPriceParams, hotelID, onFetchStaticRoomPrices, onSetViewModel],
	);

	const onChangeStaticPriceParams = useCallback(async () => {
		if (inUse) {
			deactivateStaticPriceParams();
		} else {
			await initializeStaticPriceParams();
		}
	}, [inUse, deactivateStaticPriceParams, initializeStaticPriceParams]);

	const onChangeStaticPriceAmountByRoomIDAndAdultNum = useCallback(
		({ roomID, adultNum, amount }: { roomID: string; adultNum: number; amount: number | undefined }) => {
			onSetViewModel([
				{
					key: "staticPriceParams",
					value: {
						staticRoomPrices: {
							isEdited: true,
							hasError: false,
							value:
								staticPriceParams?.staticRoomPrices.value.map((item) => {
									if (item.roomID.value === roomID) {
										return {
											...item,
											prices: {
												isEdited: true,
												hasError: false,
												value: item.prices?.value?.map((price) => {
													if (price.adultNum.value === adultNum) {
														return {
															...price,
															price: {
																amount: {
																	isEdited: true,
																	value: amount!,
																},
																currencyType: {
																	isEdited: true,
																	value: CurrencyType.Jpy,
																},
															},
														};
													}

													return price;
												}),
											},
										};
									}

									return item;
								}) ?? [],
						},
					} satisfies ForAutoGenerator<
						InputMaybe<StaticRoomPriceParamsInput>,
						"hotelID" | "planID" | "hotelPolicy" | "hotel"
					>,
				},
			]);
		},
		[onSetViewModel, staticPriceParams?.staticRoomPrices?.value],
	);

	return {
		hotelID,
		staticPriceParams,
		inUse,
		onChangeStaticPriceParams,
		initializeStaticPriceParams,
		onChangeStaticPriceAmountByRoomIDAndAdultNum,
	};
};
